import React, { useEffect, useState, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Input, 
  Select, 
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  IconButton,
  Grid,
  Card,
  Avatar,
  Chip,
  Divider,
  Switch
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext.jsx';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';
import { v4 as uuidv4 } from 'uuid';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userMetadata } = useContext(AuthContext);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userType, setUserType] = useState('');
  const [roleId, setRoleId] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientIds, setClientIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [notification, setNotification] = useState('no');
  const [showOnStatusBoard, setShowOnStatusBoard] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      const { data: clientData, error: clientError } = await supabase.from('clients').select('id, name');
      if (!clientError) setClients(clientData);
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('id, role_name');
      if (!roleError) setRoles(roleData);
      else console.error('Error fetching roles:', roleError);
    };
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        user_clients (
          client_id,
          clients (
            id,
            name
          )
        )
      `);

    if (!error) {
      // Map the joined clients into a flat structure for display
      const processed = data.map(user => {
        const clientList = user.user_clients?.map(uc => uc.clients?.name).filter(Boolean);
        return {
          ...user,
          client_names: clientList,
        };
      });
      // Sort users alphabetically by display_name
      const sortedUsers = processed.sort((a, b) => {
        const nameA = (a.display_name || a.email || '').toLowerCase();
        const nameB = (b.display_name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setUsers(sortedUsers);
    } else {
      console.error('Error fetching users:', error);
    }
    setLoading(false);
  };

  const internalUsers = users.filter(u => u.user_type !== 'client');
  const clientUsers = users.filter(u => u.user_type === 'client');
  
  // Helper functions
  const getUserInitials = (displayName, email) => {
    if (displayName) {
      return displayName.split(' ').map(word => word[0]).join('').toUpperCase();
    }
    return email?.split('@')[0]?.substring(0, 2)?.toUpperCase() || 'U';
  };

  const getUserTypeColor = (userType) => {
    switch (userType) {
      case 'super-admin': return '#dc3545';
      case 'admin': return '#fd7e14';
      case 'staff': return '#00A884';
      case 'client': return '#6c757d';
      default: return '#495057';
    }
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'super-admin': return 'admin_panel_settings';
      case 'admin': return 'manage_accounts';
      case 'staff': return 'badge';
      case 'client': return 'business';
      default: return 'person';
    }
  };

  // Statistics
  const totalUsers = users.length;
  const activeAdmins = users.filter(u => ['super-admin', 'admin'].includes(u.user_type)).length;
  const staffUsers = users.filter(u => u.user_type === 'staff').length;
  const clientUsersCount = users.filter(u => u.user_type === 'client').length;

  // Add user
  const handleAddUser = async () => {
    const body = {
      email,
      password,
      display_name: displayName,
      user_type: userType,
      role_id: roleId,
      job_title: jobTitle,
      department,
      phone,
      notification,
    };
    if (userType === 'client') {
      body.client_ids = clientIds;
    }
    const response = await fetch('/.netlify/functions/createUser', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...body,
        redirectTo: 'https://thegrid.fldandp.com/auth/callback'
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('Error creating Supabase Auth user:', result);
      return;
    }

    if (!result.user || !result.user.id) {
      console.error('User ID not returned from createUser function');
      return;
    }

    const newUser = {
      id: result.user.id,
      email,
      display_name: displayName,
      user_type: userType,
      ...(roleId && { role_id: roleId }),
      job_title: jobTitle,
      department,
      phone,
      notification,
    };
    if (userType === 'client') {
      newUser.client_ids = clientIds;
      console.log('Client IDs being saved:', clientIds);
    }

    const { data: existing, error: fetchError } = await supabase
      .from('users')
      .select('id')
      .eq('id', newUser.id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error checking user existence:', fetchError);
      return;
    }

    if (!existing) {
      const { data, error } = await supabase.from('users').insert([newUser]);
      console.log('Insert result:', data, error);

      if (error) {
        console.error('Error inserting into users table:', error.message);
        return;
      }

      // Insert into user_clients if needed
      if (userType === 'client' && clientIds.length > 0) {
        const { error: linkError } = await supabase
          .from('user_clients')
          .insert(clientIds.map(client_id => ({
            user_id: newUser.id,
            client_id,
          })));
        if (linkError) {
          console.error('Error linking user to clients:', linkError);
          return;
        }
      }
    }

    await fetchUsers(); // refresh the user list
    setDialogOpen(false);
    clearForm();
  };

  // Edit user
  const handleEditUser = async () => {
    if (password) {
      const passwordResponse = await fetch('/.netlify/functions/updateUserPassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: editingUserId,
          password,
        }),
      });
      const passwordResult = await passwordResponse.json();
      if (!passwordResponse.ok) {
        console.error('Error updating password:', passwordResult);
        return;
      }
      setPassword('');
    }

    const updateFields = {
      email,
      display_name: displayName,
      user_type: userType,
      ...(jobTitle && { job_title: jobTitle }),
      ...(department && { department }),
      ...(phone && { phone }),
      ...(roleId && { role_id: roleId }),
      notification,
      show_on_status_board: showOnStatusBoard,
    };
    console.log("Updating user with:", updateFields);

    const { error } = await supabase
      .from('users')
      .update(updateFields)
      .eq('id', editingUserId);

    if (!error) {
      // Sync client links
      if (userType === 'client') {
        const { error: deleteError } = await supabase
          .from('user_clients')
          .delete()
          .eq('user_id', editingUserId);

        if (deleteError) {
          console.error('Error removing old client links:', deleteError);
          return;
        }

        if (clientIds.length > 0) {
          const { error: insertError } = await supabase
            .from('user_clients')
            .insert(clientIds.map(client_id => ({
              user_id: editingUserId,
              client_id,
            })));

          if (insertError) {
            console.error('Error inserting new client links:', insertError);
            return;
          }
        }
      }

      fetchUsers();
      setEditingUserId(null);
      setDialogOpen(false);
      clearForm();
    }
  };

  const clearForm = () => {
    setEmail('');
    setDisplayName('');
    setUserType('');
    setRoleId('');
    setJobTitle('');
    setDepartment('');
    setPhone('');
    setPassword('');
    setClientIds([]);
    setNotification('no');
    setShowOnStatusBoard(false);
  };

  const openEditDialog = (user) => {
    setEmail(user.email || '');
    setDisplayName(user.display_name || '');
    setUserType(user.user_type || '');
    setRoleId(user.role_id || '');
    setJobTitle(user.job_title || '');
    setDepartment(user.department || '');
    setPhone(user.phone || '');
    setClientIds(user.user_clients?.map(uc => uc.client_id).filter(Boolean) || []);
    setNotification(user.notification || 'no');
    setShowOnStatusBoard(user.show_on_status_board || false);
    setEditingUserId(user.id);
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    clearForm();
    setEditingUserId(null);
    setDialogOpen(true);
  };

  // Delete user
  const handleDeleteUser = async (id) => {
    const { error } = await supabase.from('users').delete().eq('id', id);
    if (!error) fetchUsers();
  };


  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Tables Loading */}
        <CardSkeleton />
        <Box sx={{ mt: 3 }}>
          <CardSkeleton />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* User Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            group
          </span>
          User Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Users"
              value={totalUsers}
              icon="group"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Administrators"
              value={activeAdmins}
              icon="admin_panel_settings"
              gradientColors={['#fd7e14', '#e8570c']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Staff Members"
              value={staffUsers}
              icon="badge"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Client Users"
              value={clientUsersCount}
              icon="business"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add User Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                person_add
              </span>
              User Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage user accounts and permissions
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={openAddDialog}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add New User
          </EnhancedButton>
        </Box>
      </Card>

      {/* Internal Users Section */}
      <Box sx={{ mb: 4 }} className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            badge
          </span>
          Internal Users ({internalUsers.length})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'user_info', label: 'User', width: 300 },
            { key: 'user_type', label: 'Role', width: 150 },
            { key: 'job_info', label: 'Position', width: 200 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}
          rows={internalUsers.map((user) => ({
            user_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getUserTypeColor(user.user_type),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getUserInitials(user.display_name, user.email)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {user.display_name || 'No Name'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {user.email}
                  </Typography>
                </Box>
              </Box>
            ),
            user_type: (
              <Chip 
                size="sm" 
                variant="soft" 
                sx={{
                  bgcolor: `${getUserTypeColor(user.user_type)}20`,
                  color: getUserTypeColor(user.user_type)
                }}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getUserTypeIcon(user.user_type)}
                  </span>
                }
              >
                {user.user_type?.replace('-', ' ')?.toUpperCase() || 'Unknown'}
              </Chip>
            ),
            job_info: (
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                  {user.job_title || 'No Title'}
                </Typography>
                {user.department && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {user.department} Dept.
                  </Typography>
                )}
              </Box>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => openEditDialog(user)}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDeleteUser(user.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>

      {/* Client Users Section */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            business
          </span>
          Client Users ({clientUsers.length})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'user_info', label: 'User', width: 300 },
            { key: 'clients', label: 'Associated Clients', width: 250 },
            { key: 'contact', label: 'Contact', width: 150 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}
          rows={clientUsers.map((user) => ({
            user_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#6c757d',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getUserInitials(user.display_name, user.email)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {user.display_name || 'No Name'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {user.email}
                  </Typography>
                </Box>
              </Box>
            ),
            clients: (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {user.client_names?.length > 0 ? (
                  user.client_names.slice(0, 2).map((client, i) => (
                    <Chip key={i} size="sm" variant="soft" color="primary">
                      {client}
                    </Chip>
                  ))
                ) : (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    No clients assigned
                  </Typography>
                )}
                {user.client_names?.length > 2 && (
                  <Chip size="sm" variant="soft" color="neutral">
                    +{user.client_names.length - 2}
                  </Chip>
                )}
              </Box>
            ),
            contact: (
              <Typography level="body-sm">
                {user.phone || 'No Phone'}
              </Typography>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => openEditDialog(user)}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDeleteUser(user.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>
      {/* Enhanced User Modal */}
      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <ModalDialog 
          sx={{ 
            maxWidth: '600px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingUserId ? 'edit' : 'person_add'}
            </span>
            {editingUserId ? 'Edit User' : 'Add New User'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    email
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="John Doe"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    person
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label={editingUserId ? "New Password" : "Password"}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required={!editingUserId}
                helperText={editingUserId ? "Leave blank to keep current password" : undefined}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    lock
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>User Type</Typography>
                <Select 
                  value={userType} 
                  onChange={(e, val) => setUserType(val)} 
                  placeholder="Select user type"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="super-admin">Super Admin</Option>
                  <Option value="admin">Admin</Option>
                  <Option value="staff">Staff</Option>
                  <Option value="client">Client</Option>
                </Select>
              </Box>
            </Grid>
            
            {userType === 'client' && (
              <Grid xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Associated Clients</Typography>
                  <Select
                    multiple
                    value={clientIds}
                    onChange={(e, val) => setClientIds(val)}
                    placeholder="Select client(s)"
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    {clients.map((client) => (
                      <Option key={client.id} value={client.id}>{client.name}</Option>
                    ))}
                  </Select>
                </Box>
              </Grid>
            )}
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Job Title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Software Engineer"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    work
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Department</Typography>
                <Select 
                  value={department} 
                  onChange={(e, val) => setDepartment(val)} 
                  placeholder="Select department"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="Survey">Survey</Option>
                  <Option value="Engineering">Engineering</Option>
                  <Option value="Admin">Admin</Option>
                  <Option value="GIS">GIS</Option>
                </Select>
              </Box>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    phone
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>User Role</Typography>
                <Select 
                  value={roleId} 
                  onChange={(e, val) => setRoleId(val)} 
                  placeholder="Select role"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  {roles.map(role => (
                    <Option key={role.id} value={role.id}>{role.role_name}</Option>
                  ))}
                </Select>
              </Box>
            </Grid>
            
            <Grid xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>
                  Permit Expiration Notifications
                </Typography>
                <Select 
                  value={notification} 
                  onChange={(e, val) => setNotification(val)} 
                  placeholder="Enable notifications?"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="yes">Yes - Send notifications</Option>
                  <Option value="no">No - Disable notifications</Option>
                </Select>
              </Box>
            </Grid>
            
            {/* Status Board Toggle - only show for non-client users */}
            {userType && userType !== 'client' && (
              <Grid xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>
                    Status Board Inclusion
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Switch
                      checked={showOnStatusBoard}
                      onChange={(e) => setShowOnStatusBoard(e.target.checked)}
                      size="md"
                      color="success"
                      sx={{
                        '--Switch-thumbSize': '18px',
                        '--Switch-trackWidth': '42px',
                        '--Switch-trackHeight': '24px',
                      }}
                    />
                    <Typography level="body-sm" sx={{ 
                      color: showOnStatusBoard ? 'success.600' : 'text.secondary',
                      fontWeight: showOnStatusBoard ? 500 : 400 
                    }}>
                      {showOnStatusBoard ? 'User will appear on Status Board' : 'User will not appear on Status Board'}
                    </Typography>
                  </Box>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                    Control whether this user appears as a column on the Status Board for task assignment
                  </Typography>
                </Box>
              </Grid>
            )}
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={editingUserId ? handleEditUser : handleAddUser}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingUserId ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingUserId ? 'Update User' : 'Add User'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default UserManager;
